import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface Submission {
  id: string;
  type: 'farmer_registration' | 'farm_price';
  timestamp: string;
  data: any;
  status: 'success' | 'failed' | 'pending';
  error?: string;
}

interface SubmissionHistoryProps {
  onRetry?: (submission: Submission) => void;
}

export const SubmissionHistory: React.FC<SubmissionHistoryProps> = ({ onRetry }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const submissions: Submission[] = JSON.parse(
    localStorage.getItem('coordinator_submissions') || '[]'
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-warning" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      success: 'default' as const,
      failed: 'destructive' as const,
      pending: 'secondary' as const
    };
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {status}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getSubmissionSummary = (submission: Submission) => {
    if (submission.type === 'farmer_registration') {
      return `${submission.data.farmerName} (${submission.data.region})`;
    } else {
      return `${submission.data.productName} - ${submission.data.price} ETB/kg`;
    }
  };

  if (submissions.length === 0) {
    return null;
  }

  return (
    <Card className="mt-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">
                Recent Submissions ({submissions.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {submissions.filter(s => s.status === 'success').length} successful
                </Badge>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {submissions.slice(0, 5).map((submission) => (
                <div 
                  key={submission.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {getStatusIcon(submission.status)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">
                        {submission.type === 'farmer_registration' ? '👨‍🌾 Farmer' : '💰 Price'}
                      </span>
                      {getStatusBadge(submission.status)}
                    </div>
                    
                    <p className="text-sm text-muted-foreground truncate">
                      {getSubmissionSummary(submission)}
                    </p>
                    
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(submission.timestamp)}
                      </span>
                      
                      {submission.status === 'failed' && onRetry && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onRetry(submission)}
                          className="h-6 px-2 text-xs"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Retry
                        </Button>
                      )}
                    </div>
                    
                    {submission.status === 'failed' && submission.error && (
                      <p className="text-xs text-destructive mt-1 bg-destructive/10 p-2 rounded">
                        {submission.error}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 text-xs text-muted-foreground">
                    ID: {submission.id.split('-')[1]}
                  </div>
                </div>
              ))}
              
              {submissions.length > 5 && (
                <p className="text-xs text-center text-muted-foreground">
                  Showing 5 of {submissions.length} submissions
                </p>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};